import { QuestionTranslate } from '@repo/lib/quiz/questions/class/QuestionTranslate';
import { PencilLineIcon } from 'lucide-react';
import { useMemo } from 'react';

import {
  QuestionMarkedDescription,
  QuestionMarkedHeader,
  QuestionMarkedNoAnswer,
  QuestionMarkedQuestionContent,
  QuestionMarkedUserAnswer,
  QuestionMarkedWrapper,
} from '@/components/features/quiz/question/question-marked';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { useQuestionMarkedForm } from '@/hooks/questions';

import type { QuestionMarkedTranslateProps } from '.';

import { QuestionMarkedTranslateContent, QuestionMarkedTranslateForm } from '.';

export function QuestionMarkedTranslate(questionMarkedTranslateProps: QuestionMarkedTranslateProps) {
  const { marked, question, showMarked, showQuestion, answer, onEditMark } = questionMarkedTranslateProps;
  const { formRef, wrapperRef, openForm, setOpenForm, openFormHandler, closeFormHandler } = useQuestionMarkedForm();

  const questionObj = useMemo<QuestionTranslate>(() => {
    const qClass = new QuestionTranslate(question, null, marked);

    if (answer) qClass.importAnswer(answer);

    return qClass;
  }, [question, marked, answer]);

  const correctState = questionObj.correctState();
  const score: number = questionObj.getPoints();
  const totalPoints: number = questionObj.getMaxPoints();
  const correct: boolean = questionObj.getMarked()?.correct ?? false;

  if (!showMarked) return null;

  if (!marked || !questionObj.isMarked()) return <QuestionMarkedNoAnswer showMarked={showMarked} />;
  return (
    <QuestionMarkedWrapper showMarked={showMarked} ref={wrapperRef}>
      <QuestionMarkedHeader
        score={score}
        totalPoints={totalPoints}
        correct={correct}
        correctType={correctState}
        headerActions={
          onEditMark && (
            <Button
              variantColor={correctState === 'waiting' ? 'destructive' : 'default'}
              variant={correctState === 'waiting' ? 'secondary' : 'tertiary'}
              size="sm"
              disabled={openForm}
              onClick={openFormHandler}
            >
              <PencilLineIcon />
              Oceń
            </Button>
          )
        }
      />
      <QuestionMarkedQuestionContent showQuestion={showQuestion} content={question.content}>
        <QuestionMarkedTranslateContent question={question} />
      </QuestionMarkedQuestionContent>
      {marked.ids && !openForm && (
        <QuestionMarkedUserAnswer>
          <div className="space-y-2">
            <p className="text-accent-foreground text-sm font-medium">Odpowiedziano:</p>
            <div className="flex flex-wrap gap-1">
              {marked.ids.map((id) => (
                <Badge variant={correct ? 'success' : 'destructive'} size={'sm'} key={id}>
                  {question.options.find((option) => option.id === id)?.content}
                </Badge>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-accent-foreground text-sm font-medium">Wyjaśnienie:</p>
            <p className="text-muted-foreground text-sm">{marked.explanation ? marked.explanation : 'Brak wyjaśnienia'}</p>
          </div>
        </QuestionMarkedUserAnswer>
      )}
      <Collapsible open={openForm} onOpenChange={setOpenForm} className="-my-px">
        <CollapsibleContent ref={formRef}>
          <QuestionMarkedDescription />
          <QuestionMarkedTranslateForm
            onSubmit={onEditMark}
            onClose={closeFormHandler}
            marked={questionObj.getMarked()}
            content={questionObj.getContent()}
            maxPoints={questionObj.getMaxPoints()}
            isOpen={openForm}
          />
        </CollapsibleContent>
      </Collapsible>
    </QuestionMarkedWrapper>
  );
}
